import { useCallback, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DragHandleOutlinedIcon from '@mui/icons-material/DragHandleOutlined';
import Tooltip from './Tooltip';
import modalStyles from './modalStyles';
import {
  clampModalHeight,
  readStoredModalHeight,
  storeModalHeight,
} from '../../helpers/modal/modalHeight';

// Module-level — setAppElement is a one-time init; modalStyles is a stable
// object, so nothing per-render.
ReactModal.setAppElement('#root');
// De marge staat in modalStyles.js, samen met het waarom: hij was op negen
// plekken overgenomen en op alle negen fout. Zie docs/inbox.md Bug #267 en #268.
export { modalStyles };

/** Hoeveel pixels een pijltjestoets het venster verzet. */
const KEYBOARD_STEP = 40;

// AI-CONTEXT: See docs/pitfalls.md#ui-removed-prop-resurrects-default
// Before removing or renaming ANY prop below, grep the prop name across src/ and
// migrate every call site in the same commit. React ignores unknown props
// silently, so a stranded consumer gets no error — it just falls back to the
// default, and for a `hide*` flag that default puts the hidden UI back on screen.
const Modal = ({
  modalOpen,
  setModalOpen,
  confirm,
  confirmDisabled,
  pageHeader,
  description,
  content,
  actions,
  hideConfirm = false,
  confirmLabel = 'Opslaan',
  cancelLabel = 'Annuleren',
  blocking = false,
}) => {

  // De hoogte die deze gebruiker zelf heeft ingesteld. `null` = de
  // standaardhoogte uit de stylesheet; dat is bewust iets ANDERS dan een
  // hoogte van 0, en daarom geen getal met een nulwaarde.
  const [height, setHeight] = useState(/** @type {number | null} */ (null));
  /** @type {React.RefObject<HTMLDivElement>} */
  const containerRef = useRef(null);

  // Bij het openen ophalen, niet bij het laden van de module: het scherm kan
  // sinds de vorige keer van formaat zijn veranderd, en readStoredModalHeight
  // knipt de bewaarde hoogte op het scherm van nu.
  useEffect(() => {
    if (!modalOpen) return;
    setHeight(readStoredModalHeight(window.innerHeight));
  }, [modalOpen]);

  /**
   * Zet de hoogte en bewaart hem. Eén plek, zodat slepen en de pijltjestoetsen
   * niet uit elkaar kunnen lopen.
   *
   * @param {number} volgende - Gevraagde hoogte in pixels
   * @returns {void}
   */
  const applyHeight = useCallback((volgende) => {
    const toegestaan = clampModalHeight(volgende, window.innerHeight);
    if (toegestaan === null) return;
    setHeight(toegestaan);
    storeModalHeight(toegestaan, window.innerHeight);
  }, []);

  /** De hoogte die het venster NU heeft, gemeten; niet wat we dachten dat hij was. */
  const currentHeight = () => containerRef.current?.getBoundingClientRect().height || 0;

  /**
   * Slepen aan de greep onderaan.
   *
   * Pointer events en geen mouse events: dan werkt het ook met een pen of een
   * aanraakscherm, en `setPointerCapture` houdt het slepen heel als de muis
   * buiten de greep raakt — anders blijft het venster halverwege hangen.
   *
   * `e.clientY` wordt DIRECT uitgelezen en niet in een updater: het event is
   * tegen die tijd al opgeruimd.
   * AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
   *
   * @param {React.PointerEvent<HTMLButtonElement>} e
   * @returns {void}
   */
  const startResize = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = currentHeight();
    const greep = e.currentTarget;

    const onMove = (ev) => applyHeight(startHeight + (ev.clientY - startY));
    const onUp = () => {
      greep.removeEventListener('pointermove', onMove);
      greep.removeEventListener('pointerup', onUp);
      greep.removeEventListener('pointercancel', onUp);
    };

    greep.setPointerCapture(e.pointerId);
    greep.addEventListener('pointermove', onMove);
    greep.addEventListener('pointerup', onUp);
    greep.addEventListener('pointercancel', onUp);
  };

  /**
   * Hetzelfde met het toetsenbord. Een greep die alleen met de muis werkt sluit
   * iedereen uit die geen muis gebruikt, en er is geen andere weg naar meer
   * werkruimte.
   * AI-CONTEXT: See docs/pitfalls.md#ui-hover-only-affordance-dies-on-mobile
   *
   * @param {React.KeyboardEvent<HTMLButtonElement>} e
   * @returns {void}
   */
  const resizeByKey = (e) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    applyHeight(currentHeight() + (e.key === 'ArrowDown' ? KEYBOARD_STEP : -KEYBOARD_STEP));
  };

  // `maxHeight` gaat mee met `height`: de stylesheet zet er zelf een op
  // (.ReactModal__Content), en twee bovengrenzen die allebei een getal kiezen
  // lopen uiteen zodra er één verandert. Zo is er er maar één, en die staat in
  // modalHeight.js.
  // AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
  const style = height === null
    ? modalStyles
    : { content: { ...modalStyles.content, height: `${height}px`, maxHeight: `${height}px` } };

  // `blocking` governs DISMISSAL only — it hides the header close button and
  // disables ESC/backdrop, for modals the user must act on (e.g. ConsentGate).
  // It does not touch the footer: pair it with `actions={null}` when the
  // content owns its own submit button, or the default footer still renders.
  //
  // `actions` overrides the default Annuleren/Opslaan footer.
  // Pass `actions={null}` to hide the footer entirely (e.g. when the content
  // renders its own submit button).
  const footer = actions === undefined ? (
    <>
      {/* Zonder Opslaan is deze knop niet meer 'afbreken' maar 'klaar' — en de
          rode kleur van #modal-cancel-button leest dan als gevaarlijk.
          `modal-lone-action` zet hem terug naar de normale knopkleur. */}
      <button
        id='modal-cancel-button'
        className={hideConfirm ? 'modal-lone-action' : undefined}
        onClick={() => setModalOpen(false)}
      >{cancelLabel}</button>
      {!hideConfirm && (
        <button id='modal-save-button' onClick={confirm} disabled={confirmDisabled}>{confirmLabel}</button>
      )}
    </>
  ) : actions

  return (
    <ReactModal
    isOpen={modalOpen}
    onRequestClose={() => setModalOpen(false)}
    shouldCloseOnEsc={!blocking}
    shouldCloseOnOverlayClick={!blocking}
    style={style}
    contentLabel={pageHeader}
    >
        <div id='modal-container' ref={containerRef}>
            <div className="modal-header">
                <div className="modal-header-text">
                    <h2>{pageHeader}</h2>
                    {description && <p className="modal-header-description">{description}</p>}
                </div>
                {!blocking && (
                    <Tooltip content="Sluiten" top="-30px">
                        <button
                            type="button"
                            className="modal-close-button"
                            onClick={() => setModalOpen(false)}
                            aria-label="Sluiten"
                        >
                            <CloseOutlinedIcon fontSize="small" />
                        </button>
                    </Tooltip>
                )}
            </div>
            <div className="modal-content">
                {content}
            </div>
            {footer && (
                <div id='modal-button-container'>
                    {footer}
                </div>
            )}
            <Tooltip content="Sleep om het venster hoger of lager te maken" top="-30px">
                <button
                    type="button"
                    className="modal-resize-handle"
                    aria-label="Hoogte van het venster aanpassen"
                    onPointerDown={startResize}
                    onKeyDown={resizeByKey}
                >
                    <DragHandleOutlinedIcon fontSize="small" />
                </button>
            </Tooltip>
        </div>
    </ReactModal>
  )
}

export default Modal
